// TermsOfService.js - Page component for displaying the Terms of Service.
// This functional component dynamically loads Markdown content based on the current language (e.g., termsOfService.en.md or termsOfService.es.md).
// Uses react-markdown for proper formatting (headings, lists, etc.), with custom link components to open links in new tabs for better UX and security.
// Supports theme switching and includes a back button for navigation.
// Handles loading and error states for robustness.
// Updated: Removed redundant back button at the bottom (now handled by NavBar for consistency).
import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown'; // For rendering Markdown
import { useGlobalContext } from '../hooks/useGlobalContext';

const TermsOfService = () => {
  const { t, i18n } = useTranslation(); // Access i18n for language detection
  const { theme } = useGlobalContext();
  const [content, setContent] = useState(''); // State to hold loaded Markdown content
  const [loading, setLoading] = useState(true); // Loading state for UX

  useEffect(() => {
    const loadContent = async () => {
      try {
        const lang = i18n.language;
        const response = await fetch(`/locales/termsOfService.${lang}.md`); // Fetch from public/
        if (!response.ok) throw new Error('Failed to load content');
        const text = await response.text();
        setContent(text);
      } catch (error) {
        console.error('Error loading Terms of Service Markdown:', error); // Fixed: Corrected error message from "Privacy Policy"
        setContent(t('contentNotFound', { defaultValue: 'Content not available.' }));
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, [i18n.language, t]); // Updated: Added 't' to dependency array to fix ESLint exhaustive-deps warning (t is used in catch block and is stable via react-i18next)

  // Custom components for ReactMarkdown to enhance link rendering
  const markdownComponents = {
    a: ({ children, href }) => (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer" 
        className={theme === 'dark' ? 'text-light' : 'text-primary'} // Theme-aware link styling
      >
        {children}
      </a>
    ),
  };

  // Show loading indicator while content is being fetched (consistent with PrivacyPolicy.js)
  if (loading) {
    return (
      <Container fluid className={`py-5 ${theme === 'dark' ? 'bg-dark text-light' : 'bg-light text-dark'}`}>
        <div className="text-center">{t('loading')}</div>
      </Container>
    );
  }

  return (
    <Container fluid className={`py-5 ${theme === 'dark' ? 'bg-dark text-light' : 'bg-light text-dark'}`}>
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={8}>
          <h1 className="text-center mb-4">{t('footer.termsOfService')}</h1>
          {/* Render Markdown with react-markdown for proper formatting, including custom link components */}
          <ReactMarkdown components={markdownComponents} className="markdown-content">
            {content}
          </ReactMarkdown>
          {/* Back button removed: Navigation now handled by NavBar for consistency and better UX */}
        </Col>
      </Row>
    </Container>
  );
};

export default TermsOfService;